/* Sticky mobile add-to-cart
   Project: Veryplants CRO fixes (Apr 2026)
   Subscribes to the theme's pubsub for variant changes (constants.js: PUB_SUB_EVENTS).
*/

(function () {
  if (customElements.get('sticky-atc')) return;

  class StickyATC extends HTMLElement {
    constructor() {
      super();
      this.priceEl = this.querySelector('[data-price]');
      this.variantEl = this.querySelector('[data-variant-title]');
      this.addBtn = this.querySelector('[data-add-to-cart]');
      this.addLabel = this.querySelector('.sticky-atc__add-label');
      this.scrollBtn = this.querySelector('[data-scroll-to-top]');
      this.productHandle = this.dataset.productHandle;
      this.productUrl = this.dataset.productUrl;
      this.showAfter = Number(this.dataset.showAfter) || 250;
      this.currentVariantId = this.addBtn?.dataset.variantId
        ? Number(this.addBtn.dataset.variantId)
        : null;
      this.product = null;
      this.isVisible = false;
      this.unsubscribe = null;
    }

    connectedCallback() {
      // If another <sticky-atc> already exists earlier in the DOM, this duplicate
      // should silently disappear. Prevents "ADDADD" double-render when a section
      // update or app re-injects a second instance.
      const existing = document.querySelectorAll('sticky-atc');
      if (existing.length > 1 && existing[0] !== this) {
        try { this.remove(); } catch (e) {}
        return;
      }
      this.dedupeSlots();
      this.fetchProduct();
      this.bindEvents();
      this.observeMainATC();
      this.toggleVisible(false);
    }

    disconnectedCallback() {
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
      this.purchaseObserver?.disconnect();
      this.mainAtcObserver?.disconnect();
      this.productInfo?.removeEventListener('change', this.onPurchaseChange);
      if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
    }

    async fetchProduct() {
      try {
        const r = await fetch(`${this.productUrl}.js`);
        if (r.ok) this.product = await r.json();
      } catch (e) {
        // Bar still works using the server-rendered initial values.
      }
      // Sync once on init so the bar reflects the variant pre-selected via URL
      // (e.g. Klaviyo Notify-me links land with ?variant=<sold-out-id>).
      // Without this, the bar stays in the default ADD state for sold-out
      // variants and creates rage clicks when users tap a non-functional button.
      this.syncFromMainSelector();
    }

    bindEvents() {
      this.addBtn.addEventListener('click', (e) => this.handleAdd(e));
      this.scrollBtn.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });

      // Subscribe to the theme's variant-change pub/sub if present (constants.js + pubsub.js).
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(
          PUB_SUB_EVENTS.variantChange,
          (data) => {
            const v = data && (data.data?.variant || data.variant);
            if (v) this.updateVariant(v);
          }
        );
      }

      // App widgets and Dawn replace parts of the form on variant changes.
      // Observe the product area, not a reference to a replaceable hidden input.
      this.productInfo = document.querySelector('product-info');
      this.onPurchaseChange = () => {
        if (this.syncFrame) return;
        this.syncFrame = requestAnimationFrame(() => {
          this.syncFrame = null;
          this.syncFromMainSelector();
        });
      };
      if (this.productInfo) {
        this.productInfo.addEventListener('change', this.onPurchaseChange);
        this.purchaseObserver = new MutationObserver(this.onPurchaseChange);
        this.purchaseObserver.observe(this.productInfo, {
          subtree: true, childList: true, characterData: true, attributes: true,
          attributeFilter: ['value', 'disabled', 'aria-disabled', 'aria-checked', 'class'],
        });
      }
    }

    getMainButton() {
      return document.querySelector('product-info product-form button[name="add"]');
    }

    syncFromMainSelector() {
      const mainButton = this.getMainButton();
      const id = Number(mainButton?.form?.querySelector('[name="id"]')?.value);
      const variant = this.product?.variants.find((v) => v.id === id);
      if (variant) this.updateVariant(variant);
      else this.syncPurchaseDisplay();
    }

    updateVariant(variant) {
      this.currentVariantId = variant.id;
      this.addBtn.dataset.variantId = variant.id;
      // Defensive: re-resolve element refs in case DOM was re-rendered by a
      // section update (cart drawer refresh, app re-render). Stale refs would
      // mean updates land on detached nodes while a fresh duplicate sits visible.
      this.priceEl = this.querySelector('[data-price]');
      this.variantEl = this.querySelector('[data-variant-title]');
      this.addLabel = this.querySelector('.sticky-atc__add-label');
      // Clean any duplicate slots a 3rd-party script may have injected. Keep the first
      // of each slot, remove any siblings with the same selector. This is the fix for
      // the "ADDADD"/"$22.99$74.99"/concatenated-variant-titles bug seen in Propel
      // session 2602353356 on in-app browsers.
      this.dedupeSlots();
      if (this.priceEl) this.priceEl.replaceChildren(document.createTextNode(this.formatMoney(variant.price)));
      if (this.variantEl) {
        this.variantEl.replaceChildren(document.createTextNode(variant.public_title || variant.title || ''));
      }
      const available = variant.available;
      this.addBtn.disabled = !available;
      this.addBtn.setAttribute('aria-disabled', String(!available));
      if (this.addLabel) this.addLabel.replaceChildren(document.createTextNode(available ? 'ADD' : 'SOLD OUT'));
      this.syncPurchaseDisplay();
    }

    syncPurchaseDisplay() {
      const mainButton = this.getMainButton();
      if (!mainButton) return;
      // Use the app's displayed price; do not calculate or hard-code a discount.
      const selectedOption = document.querySelector('product-info .sls-option-container.sls-active');
      const price = selectedOption?.querySelector('.sls-price .money')?.textContent.trim();
      if (price && this.priceEl && this.priceEl.textContent !== price) this.priceEl.textContent = price;
      const busy = mainButton.classList.contains('loading');
      const unavailable = mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true';
      this.addBtn.disabled = unavailable;
      this.addBtn.setAttribute('aria-disabled', String(unavailable));
      this.addBtn.classList.toggle('is-loading', busy);
      const label = busy ? 'ADDING…' : mainButton.disabled ? 'SOLD OUT' : 'ADD';
      if (this.addLabel && this.addLabel.textContent !== label) this.addLabel.textContent = label;
    }

    dedupeSlots() {
      // If multiple [data-price] / [data-variant-title] / .sticky-atc__add-label
      // exist (shouldn't, but defensive), remove all but the first.
      ['[data-price]', '[data-variant-title]', '.sticky-atc__add-label'].forEach((sel) => {
        const matches = this.querySelectorAll(sel);
        if (matches.length > 1) {
          for (let i = 1; i < matches.length; i++) matches[i].remove();
        }
      });
      // If multiple [data-add-to-cart] buttons inside sticky-atc, keep first
      const buttons = this.querySelectorAll('[data-add-to-cart]');
      if (buttons.length > 1) {
        for (let i = 1; i < buttons.length; i++) buttons[i].remove();
      }
    }

    handleAdd(event) {
      event.preventDefault();
      const mainButton = this.getMainButton();
      if (!mainButton || mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true') return;
      // Use the same submit path that already supports selling_plan, quantity,
      // line-item properties, validation, cart rendering and app integrations.
      // Never fall back to an id-only request: that silently removes subscriptions.
      mainButton.click();
      this.syncPurchaseDisplay();
    }

    observeMainATC() {
      const mainAtc = document.querySelector(
        'product-info button[name="add"], product-form button[name="add"], button[name="add"][type="submit"]'
      );
      if (!mainAtc || typeof IntersectionObserver === 'undefined') {
        // Fallback: show after the user scrolls past the configured threshold.
        const onScroll = () => this.toggleVisible(window.scrollY > this.showAfter);
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return;
      }
      this.mainAtcObserver = new IntersectionObserver(
        (entries) => {
          this.toggleVisible(!entries[0].isIntersecting);
        },
        { threshold: 0.1 }
      );
      this.mainAtcObserver.observe(mainAtc);
    }

    toggleVisible(show) {
      if (show === this.isVisible) return;
      this.isVisible = show;
      this.classList.toggle('is-visible', show);
      document.body.classList.toggle('has-sticky-atc-visible', show);
    }

    formatMoney(cents) {
      if (window.Shopify && Shopify.formatMoney) {
        try {
          return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
        } catch (e) {}
      }
      return '$' + (cents / 100).toFixed(2);
    }
  }

  customElements.define('sticky-atc', StickyATC);
})();
